/**
 * Lire un flux d'évènements serveur ouvert par un POST.
 *
 * ## Pourquoi pas `EventSource`
 *
 * Il ne sait faire que des **GET sans en-tête**. Les deux vues de Diagnostic s'en servent quand
 * même, au prix du jeton passé en paramètre d'URL — acceptable là, où la requête n'a pas de corps.
 * Ça ne l'est pas ici : les données d'un dashboard, ou la demande d'une génération, ne tiennent
 * pas dans une URL, et un jeton dans une URL finit dans les journaux du proxy.
 *
 * ## ⚠️ Les deux pièges, et ils ne se voient pas à la relecture
 *
 * 1. **Le dernier fragment du tampon est presque toujours incomplet.** Un paquet TCP ne respecte
 *    aucune frontière d'évènement : `{"text":"bonj` arrive, `our"}` suit. Le traiter tout de suite
 *    lève une erreur de parsage au milieu d'un flux par ailleurs sain — et le symptôme est une
 *    génération qui s'arrête sans raison. Il reste donc **en attente** du paquet suivant.
 * 2. **Un évènement peut porter plusieurs lignes `data:`**, et la spécification demande de les
 *    recoller par des sauts de ligne. L'ancien lecteur ne prenait que la première
 *    (`match(/^data: (.*)$/m)`) : inoffensif tant que la charge sort de `JSON.stringify`, qui
 *    n'émet jamais de saut de ligne brut — mais c'est un défaut qui attendrait son heure.
 *
 * ## Ce qu'il ne fait pas
 *
 * Il ne connaît aucun nom d'évènement : `surEvenement(nom, donnees)` est appelé pour tous, et
 * c'est l'appelant qui sait ce qu'ils veulent dire. Un évènement `error` n'est **pas** transformé
 * en exception ici — chaque écran en fait quelque chose de différent (le fil de discussion l'écrit
 * dans la bulle, la génération le pose sous la jauge sans vider ce qui a déjà été écrit).
 */

/**
 * Ouvre le flux, le lit jusqu'au bout, et appelle `surEvenement` pour chaque évènement.
 *
 * @param {string} url
 * @param {object} p
 * @param {string} p.token Le jeton d'authentification, en en-tête — jamais dans l'URL.
 * @param {object} p.corps Le corps du POST, sérialisé ici.
 * @param {(nom: string, donnees: object) => void} p.surEvenement
 * @param {AbortSignal} [p.signal]
 * @param {string} [p.refusParDefaut] Le message à employer si le serveur refuse sans en donner.
 * @returns {Promise<void>} Résolue à la fin du flux. Rejetée si le serveur refuse l'ouverture,
 *   ou avec une erreur `AbortError` si l'appelant a renoncé.
 */
export async function lireFluxSse(url, {
  token, corps, surEvenement, signal, refusParDefaut = 'Le serveur a refusé la requête'
}) {
  const reponse = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(corps || {}),
    signal
  });

  /*
   * ⚠️ Un refus arrive en **code HTTP**, pas dans le flux : tout ce qui peut se refuser l'est
   * avant l'ouverture des en-têtes, côté serveur. Et c'est son `message` qu'il faut lire, pas le
   * code — « Trop de demandes à l'IA coup sur coup » dit quoi faire, « 429 » laisse devant un
   * nombre.
   */
  if (!reponse.ok || !reponse.body) {
    const motif = await reponse.json().catch(() => null);
    const erreur = new Error(motif?.message || motif?.error || `${refusParDefaut} (${reponse.status})`);
    erreur.statut = reponse.status;
    erreur.code = motif?.error || null;
    erreur.corps = motif || null;
    throw erreur;
  }

  const lecteur = reponse.body.getReader();
  const decodeur = new TextDecoder();
  let tampon = '';

  for (;;) {
    const { done, value } = await lecteur.read();
    if (done) break;

    tampon += decodeur.decode(value, { stream: true });

    // Un évènement se termine par une ligne vide. Voir le piège n°1 en tête : le dernier bloc
    // est rendu au tampon, il n'est probablement pas entier.
    const blocs = tampon.split('\n\n');
    tampon = blocs.pop() || '';

    for (const bloc of blocs) {
      const evenement = analyserUnBloc(bloc);
      if (evenement) surEvenement(evenement.nom, evenement.donnees);
    }
  }

  // La fin du flux peut laisser un bloc complet sans sa ligne vide finale — un `res.end()` qui
  // suit immédiatement le dernier `write`. Le jeter perdrait l'évènement `done`.
  const dernier = analyserUnBloc(tampon);
  if (dernier) surEvenement(dernier.nom, dernier.donnees);
}

/**
 * Un bloc brut → `{ nom, donnees }`, ou `null` s'il n'y a rien d'exploitable.
 *
 * Une charge illisible est **ignorée**, jamais levée : un évènement mal formé ne doit pas
 * interrompre un flux dont le reste est sain.
 */
function analyserUnBloc(bloc) {
  if (!bloc || !bloc.trim()) return null;

  let nom = null;
  const donnees = [];

  for (const ligne of bloc.split('\n')) {
    if (ligne.startsWith(':')) continue;            // un commentaire, souvent un battement
    if (ligne.startsWith('event:')) nom = ligne.slice(6).trim();
    // La spécification recolle les lignes `data:` par des sauts de ligne. Voir le piège n°2.
    else if (ligne.startsWith('data:')) donnees.push(ligne.slice(5).replace(/^ /, ''));
  }

  if (!nom || !donnees.length) return null;

  try {
    return { nom, donnees: JSON.parse(donnees.join('\n')) };
  } catch {
    console.warn('[SSE] Charge illisible, évènement ignoré :', nom);
    return null;
  }
}

/** L'abandon volontaire se distingue d'une panne : il ne s'affiche pas. */
export const estUnAbandon = (erreur) => erreur?.name === 'AbortError';
